// =========================================================================
// RoutingScene.transform — pure projections behind /issues/routing.
// Issues module, fork-owned (zone C).
//
// Two tabs:
//
//   Routing  — every routing decision (the seven open alerts, then every
//              flagged machine × component), filterable by cause and mine,
//              with KPIs for the operator / equipment / mixed split and how
//              many reach Operations against Maintenance.
//   Training — the operators the training engine flags, and every operator's
//              rate against their site peers as a strip per site.
//
// Transformation layer: pure functions over engine outputs. No React, no
// engine mutations.
//
// @see docs/logic/issues/routing.md
// =========================================================================

import { MINE_ORDER, MINES } from '@/versions/v1/data/core';
import type { MachineClass, MineCode } from '@/versions/v1/schema/machine';
import type {
  ConcentratedOperator,
  RootCause,
  RoutingDecision,
  SiteRole,
  TrainingNeed,
} from '@/versions/v1/schema/operations';
import { CLSNAME } from '@/versions/v1/shared/oem-engine';
import type { OperatorRate } from '@/versions/v1/shared/reliability';
import { TRAINING_DEFAULTS } from '@/versions/v1/shared/reliability';
import { ABUSE_CODE, ALARM_CODES } from '@/versions/v1/shared/seed/alarm-codes';
import { ROLE_SIDE, SITE_ROLES, siteRole } from '@/versions/v1/shared/seed/site-roles';
import { CAUSE_ORDER, decisionKey } from './RouteCards.transform';

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

export type RoutingTab = 'routing' | 'training';

export const ROUTING_TABS: readonly { id: RoutingTab; label: string }[] = [
  { id: 'routing', label: 'Routing' },
  { id: 'training', label: 'Training' },
];

/** `?tab=` → tab; anything unknown is the routing tab. */
export function parseRoutingTab(raw: string | null): RoutingTab {
  return raw === 'training' ? 'training' : 'routing';
}

// ---------------------------------------------------------------------------
// Routing rows
// ---------------------------------------------------------------------------

export type CauseFilter = RootCause | 'all';
export type MineFilter = MineCode | 'all';

export interface RoutingRow {
  readonly key: string;
  readonly decision: RoutingDecision;
  readonly machine: string;
  readonly mine: MineCode;
  readonly mineName: string;
  readonly component: string;
  readonly cause: RootCause;
  readonly confidence: number;
  readonly source: 'alert' | 'flagged';
  readonly alertId: string | null;
  /** Where a click goes: the alert page, else the machine page. */
  readonly href: string;
  /** Roles routed to, in the site-role order (operations first). */
  readonly roles: readonly SiteRole[];
  readonly toOperations: boolean;
  readonly toMaintenance: boolean;
  readonly topOperator: ConcentratedOperator | null;
  readonly events: number;
}

/**
 * One row per decision. `topOperator` is joined from the finding (by
 * machine × component) and shown only when the operator signal counts —
 * an equipment row with a named driver would read as blame.
 */
export function routingRows(
  decisions: readonly RoutingDecision[],
  findings: ReadonlyMap<string, { topOperator: ConcentratedOperator | null; operatorSignal: number; events: number }>,
): RoutingRow[] {
  return decisions.map((d) => {
    const f = findings.get(`${d.machine}|${d.component}`);
    const roleSet = new Set(d.routes.map((r) => r.role));
    const roles = SITE_ROLES.filter((r) => roleSet.has(r));
    return {
      key: decisionKey(d),
      decision: d,
      machine: d.machine,
      mine: d.mine,
      mineName: MINES[d.mine]?.name ?? d.mine,
      component: d.component,
      cause: d.cause,
      confidence: d.confidence,
      source: d.source,
      alertId: d.alertId ?? null,
      href: d.alertId ? `/issues/alerts/${d.alertId}` : `/machines/${d.machine}`,
      roles,
      toOperations: roles.some((r) => ROLE_SIDE[r] === 'operations'),
      toMaintenance: roles.some((r) => ROLE_SIDE[r] === 'maintenance'),
      topOperator: d.cause === 'operator' || d.cause === 'mixed' ? (f?.topOperator ?? null) : null,
      events: f?.events ?? 0,
    };
  });
}

/**
 * Filter by cause and mine. Sort: open alerts first (in alert order), then
 * operator-side calls (mixed, operator) before equipment, then confidence.
 */
export function filterRows(rows: readonly RoutingRow[], cause: CauseFilter, mine: MineFilter): RoutingRow[] {
  const rank: Record<RootCause, number> = { mixed: 0, operator: 1, equipment: 2, insufficient: 3 };
  return rows
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => (cause === 'all' || r.cause === cause) && (mine === 'all' || r.mine === mine))
    .sort(
      (a, b) =>
        Number(b.r.source === 'alert') - Number(a.r.source === 'alert') ||
        (a.r.source === 'alert' ? a.i - b.i : 0) ||
        rank[a.r.cause] - rank[b.r.cause] ||
        b.r.confidence - a.r.confidence ||
        a.r.machine.localeCompare(b.r.machine),
    )
    .map(({ r }) => r);
}

export interface RoutingKpis {
  readonly total: number;
  readonly alerts: number;
  readonly byCause: Readonly<Record<RootCause, number>>;
  /** Decisions that reach at least one operations role. */
  readonly toOperations: number;
  /** Decisions that reach at least one maintenance role. */
  readonly toMaintenance: number;
  /** Distinct named people across every route. */
  readonly people: number;
}

/** KPIs over the unfiltered rows — the tiles are also the cause filter. */
export function routingKpis(rows: readonly RoutingRow[]): RoutingKpis {
  const byCause: Record<RootCause, number> = { operator: 0, equipment: 0, mixed: 0, insufficient: 0 };
  const people = new Set<string>();
  for (const r of rows) {
    byCause[r.cause] += 1;
    for (const t of r.decision.routes) people.add(`${t.mine}|${t.personName}`);
  }
  return {
    total: rows.length,
    alerts: rows.filter((r) => r.source === 'alert').length,
    byCause,
    toOperations: rows.filter((r) => r.toOperations).length,
    toMaintenance: rows.filter((r) => r.toMaintenance).length,
    people: people.size,
  };
}

/** Mines that appear in the rows, in the fleet's site order. */
export function minesIn(rows: readonly RoutingRow[]): MineCode[] {
  const present = new Set(rows.map((r) => r.mine));
  return MINE_ORDER.filter((m) => present.has(m));
}

/** Causes that appear in the rows, in display order. */
export function causesIn(rows: readonly RoutingRow[]): RootCause[] {
  const present = new Set(rows.map((r) => r.cause));
  return CAUSE_ORDER.filter((c) => present.has(c));
}

/** Short role labels for dense rows. */
export const ROLE_SHORT: Readonly<Record<SiteRole, string>> = {
  'Operating Supervisor': 'Supervisor',
  Superintendent: 'Superintendent',
  'Mine Control': 'Mine Control',
  'Reliability Engineer': 'Reliability eng.',
  'Maintenance Planner': 'Planner',
};

// ---------------------------------------------------------------------------
// Training
// ---------------------------------------------------------------------------

/** A code's label, or the code. */
export function codeLabel(code: string): string {
  if (code === ABUSE_CODE) return 'Operator abuse event';
  return ALARM_CODES[code]?.label ?? code;
}

export interface TrainingRow {
  readonly need: TrainingNeed;
  readonly key: string;
  readonly mineName: string;
  readonly classLabel: string;
  readonly ratioLabel: string;
  /** Operator rate as a share of the widest bar on the page, 0–100. */
  readonly ratePct: number;
  /** Peer rate on the same scale. */
  readonly peerPct: number;
  readonly codes: readonly { code: string; label: string; count: number }[];
  readonly pLabel: string;
  /** The site's Operating Supervisor — who "Book coaching" goes to. */
  readonly supervisor: string;
}

/** The flagged operators, highest ratio first, with bars on one shared scale. */
export function trainingRows(needs: readonly TrainingNeed[]): TrainingRow[] {
  const max = Math.max(1, ...needs.map((n) => Math.max(n.ratePer100h, n.peerRate)));
  return needs.map((n) => ({
    need: n,
    key: n.operatorId,
    mineName: MINES[n.mine]?.name ?? n.mine,
    classLabel: CLSNAME[n.class as MachineClass] ?? n.class,
    ratioLabel: n.ratio >= 99 ? 'peers clean' : `${n.ratio.toFixed(1)}×`,
    ratePct: (n.ratePer100h / max) * 100,
    peerPct: (n.peerRate / max) * 100,
    codes: n.topCodes.map((c) => ({ code: c.code, label: codeLabel(c.code), count: c.count })),
    pLabel: n.pValue < 0.001 ? 'p < 0.001' : `p = ${n.pValue.toFixed(3)}`,
    supervisor: siteRole(n.mine, 'Operating Supervisor').personName,
  }));
}

/** Upper end of the strip's ratio axis; anything beyond sits on the edge. */
export const STRIP_MAX_RATIO = 8;

export interface StripPoint {
  readonly operatorId: string;
  readonly name: string;
  /** Plotted ratio, clamped to `[0, STRIP_MAX_RATIO]`. */
  readonly x: number;
  readonly ratio: number;
  readonly flagged: boolean;
  /** Deterministic vertical jitter, −1…1, so equal ratios do not stack. */
  readonly jitter: number;
  /** Name drawn beside the dot — flagged, and far enough from the next label. */
  readonly labelled: boolean;
}

/** Labels closer than this on the ratio axis would collide; the lower one is dropped. */
export const STRIP_LABEL_GAP = 1.6;

export interface SiteStrip {
  readonly mine: MineCode;
  readonly mineName: string;
  readonly operators: number;
  readonly flagged: number;
  readonly points: readonly StripPoint[];
}

/** Small stable hash → −1…1, for jitter. */
function jitterOf(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i += 1) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (((h >>> 0) % 2001) / 1000) - 1;
}

/**
 * Every operator with enough hours to judge (the training engine's
 * `minHours`), one strip per site, ratio to site peers on x. Flagged
 * operators are the training needs.
 *
 * Invariant: sites follow `MINE_ORDER`; a site with no qualifying operators
 * is dropped.
 */
export function siteStrips(rates: readonly OperatorRate[], needs: readonly TrainingNeed[]): SiteStrip[] {
  const flaggedIds = new Set(needs.map((n) => n.operatorId));
  return MINE_ORDER.map((mine) => {
    const points = rates
      .filter((r) => r.mine === mine && r.hours >= TRAINING_DEFAULTS.minHours)
      .map((r) => ({
        operatorId: r.operatorId,
        name: r.name,
        x: Math.max(0, Math.min(STRIP_MAX_RATIO, r.ratio)),
        ratio: r.ratio,
        flagged: flaggedIds.has(r.operatorId),
        jitter: jitterOf(r.operatorId),
      }))
      .map((p) => ({ ...p, labelled: false }))
      // Flagged last so they paint on top.
      .sort((a, b) => Number(a.flagged) - Number(b.flagged));
    // Label flagged operators from the highest ratio down, skipping any that
    // would sit within STRIP_LABEL_GAP of a label already placed.
    const placed: number[] = [];
    for (const p of points.filter((q) => q.flagged).sort((a, b) => b.ratio - a.ratio)) {
      if (placed.every((x) => Math.abs(x - p.x) >= STRIP_LABEL_GAP)) {
        p.labelled = true;
        placed.push(p.x);
      }
    }
    return {
      mine,
      mineName: MINES[mine]?.name ?? mine,
      operators: points.length,
      flagged: points.filter((p) => p.flagged).length,
      points,
    };
  }).filter((s) => s.operators > 0);
}
